/**
 * 用戶活動追蹤模組
 * 實現頁面訪問記錄，帶節流邏輯（同一頁面 30 秒內不重複發送）
 * API 失敗時靜默處理，不影響用戶操作
 */

import apiClient from '@/services/api'
import { useAuthStore } from '@/stores/auth'

/**
 * 節流時間間隔（毫秒）
 * 同一頁面 URL 在此時間內不會重複發送追蹤請求
 */
const THROTTLE_INTERVAL = 30 * 1000 // 30 秒

/**
 * 活動追蹤器類
 * 負責記錄用戶頁面訪問行為並發送到後端
 */
class ActivityTracker {
  /** 記錄每個頁面 URL 最後一次發送的時間戳 */
  private lastSentMap: Map<string, number> = new Map()

  /**
   * 追蹤頁面訪問
   * @param pageUrl - 訪問的頁面 URL 路徑
   *
   * 節流邏輯：同一頁面 30 秒內不重複發送
   * 僅在用戶已認證時才發送追蹤請求
   * API 失敗時靜默處理，不影響用戶操作
   */
  trackPageVisit(pageUrl: string): void {
    // 檢查用戶是否已認證
    const authStore = useAuthStore()
    if (!authStore.isAuthenticated) {
      return
    }

    // 節流檢查：同一頁面 30 秒內不重複發送
    const now = Date.now()
    const lastSent = this.lastSentMap.get(pageUrl)
    if (lastSent && now - lastSent < THROTTLE_INTERVAL) {
      return
    }

    // 更新最後發送時間
    this.lastSentMap.set(pageUrl, now)

    // 發送追蹤請求，失敗時靜默處理
    apiClient.post('/user/activity', {
      action: 'page_visit',
      page_url: pageUrl
    }).catch(() => {
      // 靜默處理錯誤，不影響用戶操作
    })
  }

  /**
   * 清除節流記錄
   * 可用於用戶登出時重置狀態
   */
  clear(): void {
    this.lastSentMap.clear()
  }
}

/** 全局活動追蹤器實例 */
export const activityTracker = new ActivityTracker()

export default activityTracker
